import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { FileText, Plus, RefreshCw, Search } from 'lucide-react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useSyncTemplates, useTemplates, type MessageTemplate, type TemplateStatus } from '../api/templates'
import { fullTime } from '../lib/format'
import { TemplatePreview } from '../components/TemplatePreview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

type Filter = 'all' | 'approved' | 'review' | 'unusable'

const statusLabel: Record<TemplateStatus, string> = {
  approved: 'Approved',
  pending: 'In review',
  rejected: 'Rejected',
  paused: 'Paused by Meta',
  disabled: 'Disabled by Meta',
  deleted: 'Deleted',
  other: 'Not usable',
}

const statusVariant: Record<TemplateStatus, 'success' | 'attention' | 'danger' | 'neutral'> = {
  approved: 'success',
  pending: 'attention',
  rejected: 'danger',
  paused: 'danger',
  disabled: 'danger',
  deleted: 'neutral',
  other: 'neutral',
}

function inFilter(t: MessageTemplate, f: Filter): boolean {
  if (f === 'approved') return t.status === 'approved'
  if (f === 'review') return t.status === 'pending'
  if (f === 'unusable') return t.status !== 'approved' && t.status !== 'pending'
  return true
}

// Every role sees the list: agents will send the approved ones, viewers see
// what customers receive. Owners and admins create and sync; the server
// refuses everyone else whatever this page shows.
export function TemplatesPage() {
  const me = useMe()
  const lib = useTemplates()
  const sync = useSyncTemplates()
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'

  const all = lib.data?.templates ?? []
  const q = query.trim().toLowerCase()
  const shown = all.filter((t) => inFilter(t, filter) && (!q || t.name.includes(q) || t.body.toLowerCase().includes(q)))
  const current = shown.find((t) => t.id === selected) ?? shown[0]

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 sm:py-8">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-1">
          <h1 className="text-2xl font-bold">Templates</h1>
          <p className="text-sm text-muted-foreground">
            Approved templates are the only messages that can start a chat or reach a customer after reply time has ended. Meta reviews each one
            before it can be sent.
          </p>
        </div>
        {admin && lib.data?.connected && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <RefreshCw className={cn(sync.isPending && 'animate-spin motion-reduce:animate-none')} aria-hidden="true" />
              {sync.isPending ? 'Syncing' : 'Sync with WhatsApp'}
            </Button>
            <Button asChild>
              <Link to="/templates/new">
                <Plus aria-hidden="true" />
                New template
              </Link>
            </Button>
          </div>
        )}
      </div>

      {sync.error && (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(sync.error as RequestFailed).message}</p>
      )}

      {lib.isPending ? (
        <div role="status" aria-label="Loading templates" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : lib.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(lib.error as RequestFailed).message}</p>
      ) : !lib.data.connected && all.length === 0 ? (
        <Empty>
          No WhatsApp account is connected yet, so there are no templates to show.{' '}
          {admin ? 'Once the account is connected, sync to bring in its templates.' : 'An owner can connect it.'}
        </Empty>
      ) : all.length === 0 ? (
        <Empty>
          No templates yet.{' '}
          {admin
            ? 'Sync with WhatsApp to bring in templates made in Meta\'s tools, or create a new one.'
            : 'An owner or admin can create them or bring them in from WhatsApp.'}
        </Empty>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <TabsList>
                <TabsTrigger value="all">All {all.length}</TabsTrigger>
                <TabsTrigger value="approved">Approved {all.filter((t) => t.status === 'approved').length}</TabsTrigger>
                <TabsTrigger value="review">In review {all.filter((t) => t.status === 'pending').length}</TabsTrigger>
                <TabsTrigger value="unusable">Not usable {all.filter((t) => inFilter(t, 'unusable')).length}</TabsTrigger>
              </TabsList>
            </Tabs>
            <label className="flex min-w-0 flex-[1_1_200px] items-center gap-2 rounded-md border border-input bg-card px-3">
              <Search className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Search templates</span>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or text" className="border-0 px-0 shadow-none focus-visible:ring-0" />
            </label>
          </div>
          {lib.data.lastSyncedAt && (
            <p className="text-xs text-muted-foreground">Last synced with WhatsApp {fullTime(lib.data.lastSyncedAt)}.</p>
          )}

          {shown.length === 0 ? (
            <Empty>No templates match. Try another filter or search.</Empty>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
              <ul className="divide-y rounded-xl border bg-card">
                {shown.map((t) => (
                  <li key={t.id}>
                    <button
                      aria-current={current?.id === t.id ? 'true' : undefined}
                      onClick={() => setSelected(t.id)}
                      className={cn(
                        'flex w-full flex-col gap-1.5 px-4 py-3 text-left hover:bg-accent',
                        current?.id === t.id && 'bg-primary-soft hover:bg-primary-soft',
                      )}
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 truncate font-semibold">{t.name}</span>
                        <Badge variant="outline">{t.language}</Badge>
                        <Badge variant="outline" className="capitalize">{t.category}</Badge>
                        <Badge variant={statusVariant[t.status]}>{statusLabel[t.status]}</Badge>
                      </span>
                      <span className="line-clamp-2 text-sm text-muted-foreground">{t.body}</span>
                      {(t.rejectedReason || !t.createdInWattool || t.hasMediaHeader) && (
                        <span className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                          {t.rejectedReason && <span className="text-destructive">Meta's reason: {t.rejectedReason}</span>}
                          {!t.createdInWattool && <span>Made in Meta's tools</span>}
                          {t.hasMediaHeader && <span>Has a media header, which wattool cannot send yet</span>}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {current && (
                <aside aria-label={`Preview of ${current.name}`} className="flex flex-col gap-2 lg:sticky lg:top-4 lg:self-start">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <FileText className="size-4" aria-hidden="true" />
                    {current.name}
                  </h2>
                  <TemplatePreview
                    header={current.header}
                    hasMediaHeader={current.hasMediaHeader}
                    body={current.body}
                    footer={current.footer}
                    buttons={current.buttons}
                  />
                  <p className="text-xs text-muted-foreground">
                    {current.bodyVariables === 0
                      ? 'No variables: every customer gets the same text.'
                      : `${current.bodyVariables} variable${current.bodyVariables === 1 ? '' : 's'} to fill when sending.`}
                  </p>
                </aside>
              )}
            </div>
          )}
        </>
      )}
    </main>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">{children}</p>
}
